/*------------------------------------------------------------------------------
   About      : Declarative permission wrapper for UI elements (RBAC v2)

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import React, { isValidElement,
                cloneElement }   from 'react'
import { usePermissions }        from 'hooks/usePermissions'
import { PermissionRequirement } from 'framework/access/accessControl'

export interface PermissionGateProps {
  /** Single action, { anyOf }, { allOf }, or a whole BFF_FEATURES.* domain requirement */
  require   : PermissionRequirement
  /** 'hide' (default) removes the children; 'disable' clones them with disabled=true */
  mode     ?: 'hide' | 'disable'
  /** Rendered instead of children when denied in 'hide' mode */
  fallback ?: React.ReactNode
  children  : React.ReactNode
}

/**
 * Usage:
 *   <PermissionGate require={BFF_FEATURES.ENVIRONMENTS.CREATE}>
 *     <Button onClick={openCreate}>Create Environment</Button>
 *   </PermissionGate>
 *
 *   <PermissionGate require={UI_PAGES.ENVIRONMENTS.ctas.edit.id} mode="disable">
 *     <MenuItem onClick={openEdit}>Edit Environment</MenuItem>
 *   </PermissionGate>
 *
 * 'disable' mode relies on the child accepting a `disabled` prop (MUI Button,
 * IconButton, MenuItem, Tab, TextField, ...). Multiple/non-element children
 * fall back to hiding.
 */
export const PermissionGate = ({ require, mode = 'hide', fallback = null, children } : PermissionGateProps) => {

  const { can } = usePermissions()

  if (can(require)) return <>{children}</>

  if (mode === 'disable' && isValidElement(children)) {
    return cloneElement(children as React.ReactElement<{ disabled ?: boolean }>, { disabled : true })
  }

  return <>{fallback}</>
}
